import { useState, useEffect } from 'react';
import { X, Download, Copy, ExternalLink, Check } from 'lucide-react';
import { WorkspaceFile } from '../../types';
import { Spinner } from '../Common';

interface FileViewerProps {
  file: WorkspaceFile;
  content?: string;
  loading?: boolean;
  cdnUrl?: string;
  onClose: () => void;
}

export function FileViewer({
  file,
  content,
  loading,
  cdnUrl,
  onClose,
}: FileViewerProps) {
  const [copied, setCopied] = useState(false);

  const isImage = /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(file.name);
  const isCode = /\.(js|ts|jsx|tsx|py|rb|go|rs|java|json|yaml|yml|md|txt|sh|css|html)$/i.test(
    file.name
  );
  const isPdf = /\.pdf$/i.test(file.name);

  const handleCopy = async () => {
    if (content) {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (cdnUrl) {
      window.open(cdnUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
      <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <h3 className="font-medium text-gray-900 truncate">{file.name}</h3>
          <div className="flex items-center gap-2">
            {isCode && (
              <button
                onClick={handleCopy}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                title="Copy content"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </button>
            )}
            <button
              onClick={handleDownload}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              title="Download"
            >
              <Download className="w-5 h-5" />
            </button>
            {cdnUrl && (
              <a
                href={cdnUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                title="Open in new tab"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Spinner size="lg" />
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center p-8 bg-gray-100">
              <img
                src={cdnUrl}
                alt={file.name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          ) : isPdf ? (
            <iframe
              src={cdnUrl}
              className="w-full h-[70vh]"
              title={file.name}
            />
          ) : isCode ? (
            <pre className="p-4 bg-gray-900 text-gray-100 overflow-auto text-sm font-mono h-[70vh]">
              <code>{content}</code>
            </pre>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <p>Preview not available for this file type</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 text-sm text-gray-500">
          {file.lastCommit && (
            <span>
              Last modified by {file.lastCommit.author}: {file.lastCommit.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
